import React, {FC, useMemo} from 'react';
import { FeedbackFilter, filterFeedback, Sentiment } from '../model/feedback';
import {Column, Row} from '../atoms/Layout';
import { FeedbackResponse, FeedbackTopic } from '../api/feedback';
import { Channel } from '../api/channels';
import {Body, Hint, Subtitle} from '../atoms/Typography';
import SentimentIcon from '../atoms/SentimentIcon/index';
import styled from 'styled-components';
import colors from '../atoms/colors';
import { TextInput } from '../atoms/Input';
import Pill from '../atoms/Pill';

const FeedbackFiltersColumn = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: 300px;
    gap: 16px;
`

const Divider = styled.div`
    width: 100%;
    border-bottom: 1px solid ${colors.borderSecondary};
`;

interface Props {
    filter: FeedbackFilter;
    setFilter: (filter: FeedbackFilter) => void;
    feedback: FeedbackResponse[];
    channels: Channel[];
}

const toCategoryTitle = (topic: FeedbackTopic) => {
    if (["gameplay-mechanics", "graphics-and-design", "audio-and-sound", "performance-and-stability", "online-connectivity", "story-narrative-and-lore", "characterization", "historical-accuracy"].includes(topic.id)) {
        return "Quality Score";
    }

    if (["microtransactions-and-monetization", "gameplay-balance", "player-progression-and-rewards", "content-and-updates", "loot-boxes"].includes(topic.id)) {
        return "Fairness Score";
    }

    if (["user-interface-and-usability", "game-difficulty-and-tutorials", "controls", "accessibility"].includes(topic.id)) {
        return "Usability Score";
    }

    if (["community-interactions", "moderation-and-reporting", "customer-support", "communication-channels-and-tools", "feedback-suggestions-and-complaints"].includes(topic.id)) {
        return "Community Score";
    }

    if (['team-performance', 'player-effort', 'player-quality-recruitment', 'player-fan-interaction', 'coaching-staff-quality', 'coaching-communication', 'training-fitness', 'injury-prevention-treatment', 'season-objectives', 'competitiveness'].includes(topic.id)) {
        return "Performance Score";
    }

    if (['ownership-perception', 'boardroom-decisions', 'financial-transparency', 'club-culture', 'inclusivity-diversity', 'community-engagement', 'fan-representation', 'ambition-vs-delivery', 'commercial-balance', 'ticketing-pricing', 'merchandising', 'sponsorship-branding'].includes(topic.id)) {
        return "Club Score"
    }

    if (['accessibility', 'safety-security', 'cleanliness-facilities', 'stadium-technology', 'atmosphere', 'inclusivity-in-stadium', 'food-quality', 'food-pricing', 'hospitality-services', 'merchandise-availability'].includes(topic.id)) {
        return "Matchday Experience Score"
    }

    if (['website-app-usability', 'content-quality', 'social-media', 'behind-the-scenes-access', 'responsiveness', 'global-reach', 'inclusivity', 'fan-surveys-feedback', 'engagement-signals', 'sponsor-value-signals'].includes(topic.id)) {
        return "Digital Experience Score"
    }

    if (['pride-and-identity', 'emotional-highs', 'emotional-lows', 'club-listening', 'fan-categorisation', 'behavioural-trends', 'activism-protest', 'financial-value-perception', 'generational-loyalty', 'global-fanbase', 'emotional-roi'].includes(topic.id)) {
        return "Emotional Connection & Value Score"
    }

    return "Other"
}

interface TopicCategory {
    title: string;
    topics: FeedbackTopic[];
}

const FeedbackFilters: FC<Props> = ({ filter, setFilter, feedback, channels }) => {
    const filteredFeedback = filterFeedback(feedback, filter)
    const topics = feedback.flatMap(f => f.analysis.topics)
        .filter((topic, index, self) => self.findIndex(t => t.id === topic.id) === index)
    const topicCategories: TopicCategory[] = useMemo(() => {
        const categories: TopicCategory[] = [];
        topics.forEach(topic => {
            const title = toCategoryTitle(topic);
            const category = categories.find(c => c.title === toCategoryTitle(topic));
            if (category) {
                category.topics.push(topic);
            } else {
                categories.push({
                    title,
                    topics: [topic]
                });
            }
        });
        return categories;
    }, [topics]);

    const toggleSentiment = (sentiment: Sentiment) => {
        if (filter.sentiments?.includes(sentiment)) {
            if (filter.sentiments.length === 1) {
                setFilter({
                    ...filter,
                    sentiments: undefined
                })
                return
            }
            setFilter({
                ...filter,
                sentiments: filter.sentiments.filter(s => s !== sentiment)
            })
        } else {
            setFilter({
                ...filter,
                sentiments: [...(filter.sentiments ?? []), sentiment]
            })
        }
    }

    const toggleChannel = (channel: Channel) => {
        if (filter.channelIds?.includes(channel.id)) {
            if (filter.channelIds.length === 1) {
                setFilter({
                    ...filter,
                    channelIds: undefined
                })
                return
            }
            setFilter({
                ...filter,
                channelIds: filter.channelIds.filter(l => l !== channel.id)
            })
        } else {
            setFilter({
                ...filter,
                channelIds: [...(filter.channelIds ?? []), channel.id]
            })
        }
    }

    const toggleTopic = (topic: FeedbackTopic) => {
        if (filter.topics?.includes(topic.id)) {
            if (filter.topics.length === 1) {
                setFilter({
                    ...filter,
                    topics: undefined
                })
                return
            }
            setFilter({
                ...filter,
                topics: filter.topics.filter(l => l !== topic.id)
            })
        } else {
            setFilter({
                ...filter,
                topics: [...(filter.topics ?? []), topic.id]
            })
        }
    }

    const channelCount = (channel: Channel) => filterFeedback(feedback, { ...filter, channelIds: [channel.id] }).length
    const topicCount = (topic: FeedbackTopic) => filterFeedback(feedback, { ...filter, topics: [topic.id] }).length

    const TopicPill: FC<{ id: string, name: string }> = ({ id, name }) => {
        return <>
            <Pill
                key={id}
                active={filter.topics?.includes(id)}
                onClick={() => toggleTopic({ id, name, description: "" })}
            >
                {name} ({topicCount({ id, name, description: "" })})
            </Pill>
        </>
    }

    return <FeedbackFiltersColumn>
        <Subtitle>Displaying {filteredFeedback.length} results</Subtitle>
        <Divider />
        <Body>By sentiment</Body>
        <Row gap="small">
            <SentimentIcon sentiment={Sentiment.POSITIVE} active={filter.sentiments?.includes(Sentiment.POSITIVE)} onClick={() => toggleSentiment(Sentiment.POSITIVE)} />
            <SentimentIcon sentiment={Sentiment.NEUTRAL} active={filter.sentiments?.includes(Sentiment.NEUTRAL)} onClick={() => toggleSentiment(Sentiment.NEUTRAL)} />
            <SentimentIcon sentiment={Sentiment.NEGATIVE} active={filter.sentiments?.includes(Sentiment.NEGATIVE)} onClick={() => toggleSentiment(Sentiment.NEGATIVE)} />
        </Row>
        <Divider />
        <Body>By keyword</Body>
        <TextInput value={filter.search ?? ''} onChange={value => setFilter({ ...filter, search: value || undefined })} placeholder="Search" />
        <Divider />
        <Body>By question</Body>
        <Row gap="small">
            { channels.map(channel => <Pill
                key={channel.id}
                active={filter.channelIds?.includes(channel.id)}
                onClick={() => toggleChannel(channel)}
            >
                {channel.name} ({channelCount(channel)})
            </Pill>) }
        </Row>
        { topicCategories.length > 0 && <>
            <Divider />
            <Body>By topic</Body>
            <Column gap="small">
                {topicCategories.map(category => <>
                    <Hint key={`${category.title}-hint`}>{category.title}</Hint>
                    <Row key={`${category.title}-pills`} gap="small">
                        {category.topics.map(topic => <TopicPill key={topic.id} id={topic.id} name={topic.name}/>)}
                    </Row>
                </>)}
            </Column>
        </> }
        { topicCategories.length === 0 && <>
            <Divider />
            <Body>By topic</Body>
            <Hint>No topics found in feedback yet.</Hint>
        </>}
    </FeedbackFiltersColumn>
}

export default FeedbackFilters;
